// Доменный тип строки временного закрепления (view-model формы).
export interface IAssignmentRow {
  startDate: Date | null;
  endDate: Date | null;
  // РМ из селекта строки — permanentRmId в терминах API: ОТНОСИТЕЛЬНО кого
  // назначаем (чьи дилеры временно передаются редактируемому пользователю).
  // temporaryRmId (НА кого назначено) — сам редактируемый пользователь,
  // его сервер проставляет сам, в форме не редактируется.
  permanentRmId: number | null;
  dealers: number[];
  // Серверные поля РМ — fallback для отображения логина/направлений, когда РМ
  // не найден в available-managers (пагинация, фильтр по userId). В новой строке
  // пустые — заполнятся из selectedRm после выбора РМ.
  permanentRmName?: string | null;
  permanentRmLogin?: string | null;
  permanentRmDirections?: string[];
  // Полные объекты дилеров с сервера — fallback для options селекта дилеров,
  // чтобы выбранные теги показывали name вместо id, когда РМ не найден в
  // available-managers.
  serverDealers?: { id: number; name: string }[];
}

export const emptyRow: IAssignmentRow = {
  startDate: null,
  endDate: null,
  permanentRmId: null,
  dealers: [],
  permanentRmName: null,
  permanentRmLogin: null,
  permanentRmDirections: [],
  serverDealers: [],
};

interface INormalizedRow {
  permanentRmId: number | null;
  startDate: string | null;
  endDate: string | null;
  dealers: number[];
}

// DatePicker из ui-kit отдаёт dayjs — приводим к JS Date (new Date(dayjs)
// и moment(dayjs) дают мусор, поэтому используем dayjs.toDate()).
export function asJsDate(d: unknown): Date | null {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof (d as { toDate?: unknown }).toDate === "function")
    return (d as { toDate: () => Date }).toDate();
  const date = new Date(d as string);
  return isNaN(date.getTime()) ? null : date;
}

// Дата → YYYY-MM-DD (без TZ-сдвига от toISOString — DatePicker хранит локальное время).
export function toDayKey(d: Date | string | null | undefined): string | null {
  const date = asJsDate(d);
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Нормализация для сравнения через lodash.isEqual: отбрасываем пустые строки
// (без дилеров), сортируем дилеров внутри группы и сами группы — порядок не
// влияет на результат. Строки без РМ («Не выбран») тоже участвуют: permanentRmId
// по ТЗ необязателен, иначе добавление дилеров без РМ не считалось бы изменением
// и запрос на сохранение не уходил.
export function normalizeForCompare(rows: IAssignmentRow[]): INormalizedRow[] {
  return rows
    .filter((r) => r.dealers.length > 0)
    .map((r) => ({
      permanentRmId: r.permanentRmId ?? null,
      startDate: toDayKey(r.startDate),
      endDate: toDayKey(r.endDate),
      dealers: [...r.dealers].sort((a, b) => a - b),
    }))
    .sort(
      (a, b) =>
        (a.permanentRmId ?? 0) - (b.permanentRmId ?? 0) ||
        (a.startDate ?? "").localeCompare(b.startDate ?? "") ||
        (a.endDate ?? "").localeCompare(b.endDate ?? ""),
    );
}
